import { useEffect, useRef, useState } from "react"

const PRICINGS_TERMS_BY_PERIOD_TYPE = {
   every_7: 'weekly_terms',
   every_14: 'bi_weekly_terms',
   every_30: 'monthly_terms',
   every_60: 'bi_monthly_terms',
   every_90: 'quarterly_terms',
   every_180: 'half_year_terms',
   every_365: 'yearly_terms',
   one_time: 'one_time_terms',
}

const PRICINGS_TYPES_BY_PERIODS = {
   '7': 'every_7',
   '14': 'every_14',
   '30': 'every_30',
   '60': 'every_60',
   '90': 'every_90',
   '180': 'every_180',
   '365': 'every_365',
   'one_time': 'one_time',
}
const PERIODS_NAME_BY_PRICING_PERIOD = {
   '7': 'weekly',
   '14': 'bi-weekly',
   '30': 'monthly',
   '60': 'bi-monthly',
   '90': 'quarterly',
   '180': 'half year',
   '365': 'year',
   'one_time': 'one-time',
}
const PERIODS_CAPITALIZED_NAME_BY_PRICING_PERIOD = {
   '7': 'Weekly',
   '14': 'Biweekly',
   '30': 'Monthly',
   '60': 'Bimonthly',
   '90': 'Quarterly',
   '180': 'Semiannually',
   '365': 'Yearly',
   'one_time': 'One-time',
}
const PERIODS_NAME_WITH_DAYS_BY_PRICING_PERIOD = {
   '7': 'every 7 days',
   '14': 'every 14 days',
   '30': 'every 30 days',
   '60': 'every 60 days',
   '90': 'every 90 days',
   '180': 'every 180 days',
   '365': 'every 365 days',
   'one_time': 'One-time',
}

const PRICINGS_PERIODS_BY_TYPES = {
   'every_7': '7',
   'every_14': '14',
   'every_30': '30',
   'every_60': '60',
   'every_90': '90',
   'every_180': '180',
   'every_365': '365',
   'one_time': 'one_time',
}

const PRICINGS_PERIOD_TEXT_NAMES = {
   'every_7': 'weekly',
   'every_14': 'biweekly',
   'every_30': 'monthly',
   'every_60': 'bimonthly',
   'every_90': 'quarterly',
   'every_180': 'semiannually',
   'every_365': 'yearly',
   'one_time': 'one-time',
}

const DEFAULT_MEMBERSHIP_PERIOD = 'every_30'
const DEFAULT_MEMBERSHIPS_LIMIT = window.DEFAULT_MEMBERSHIP_LIMIT //  || 4
const COMPARE_FIRST_PAGE_COUNT = 10


const getColsCount = (breakpoint, data) => {


   if(breakpoint === 1024) {
      return 1
   }

   if(breakpoint === 1439) {

      if(data?.length < 2) {
         return 1
      }

      return 2
   }

   if(breakpoint === 1910){
      if(data?.length === 2) {
         return 2
      }
      if(data?.length === 1) {
         return 1
      }

      return 4

   }

   if(breakpoint === 'default') {
      if(data?.length === 1) {
         return 1
      }
      if(data?.length === 2) {
         return 2
      }
      if(data?.length === 3) {
         return 3
      }
      return 4
   }
}

const useImageBrightness = ({ imageUrl }) => {
   const [isDark, setIsDark] = useState(false);
   const canvasRef = useRef(null);

   useEffect(() => {
      const detectBrightness = () => {
         const canvas = canvasRef.current;
         if(!canvas) return;

         const ctx = canvas.getContext('2d');
         const img = new Image();
         img.crossOrigin = 'Anonymous'; // Essential for loading images from different origins
         img.src = imageUrl;

         img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0, img.width, img.height);

            const imageData = ctx.getImageData(0, 0, img.width, img.height);
            const data = imageData.data;
            let totalBrightness = 0;

            // Iterate through pixels and calculate average brightness
            for(let i = 0; i < data.length; i += 4) {
               const r = data[i];
               const g = data[i + 1];
               const b = data[i + 2];
               // Calculate perceived brightness using a common formula
               const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
               totalBrightness += brightness;
            }

            const averageBrightness = totalBrightness / (data.length / 4);
            // Define a threshold to determine "dark" or "light"
            setIsDark(averageBrightness < 128); // 128 is a common threshold for a 0-255 scale
         };

         img.onerror = (error) => {
            console.error("Error loading image:", error);
            setIsDark(false); // Default to light if image fails to load
         };
      };

      if(imageUrl) {
         detectBrightness();
      }
   }, [imageUrl]);

   return ({
      view: <div>
         <div style={ { backgroundColor: isDark ? 'white' : 'black', color: isDark ? 'black' : 'white', padding: '20px' } }>
            {isDark ? 'This background is dark!' : 'This background is light!'}
         </div>
         <canvas ref={ canvasRef } style={ { display: 'none' } }></canvas> {/* Hidden canvas for image processing */}
      </div>,
      isDarkBackground: isDark,
   }
   );
};

const sortBenefitsByDefault = (benefits) => {
   return benefits.sort((a, b) => {
      const getPriority = (item) => {
         if(item.is_auto === true && item.key?.includes('photosets_access_benefit')) return 1;
         if(item.is_auto === true && item.key?.includes('videos_access_benefit')) return 2;
         if(item.is_auto === true && item.key?.includes('livestreams_access_benefit')) return 3;
         if(item.is_auto === true && item.key?.includes('photosets_download_benefit')) return 4;
         if(item.is_auto === true && item.key?.includes('videos_download_benefit')) return 5;
         if(item.is_auto === true && item.key?.includes('custom_page_benefit')) return 6;
         if(item.is_auto === true && item.key?.includes('chat_text_benefit')) return 7;
         if(item.is_auto === true && item.key?.includes('chat_image_benefit')) return 8;
         if(item.is_auto === true && item.key?.includes('chat_voice_benefit')) return 9;
         if(item.is_auto !== true) return 10;
         return 11; // fallback for undefined/missing keys
      };

      return getPriority(a) - getPriority(b);
   });
};



export {
   PRICINGS_TERMS_BY_PERIOD_TYPE,
   PRICINGS_TYPES_BY_PERIODS,
   DEFAULT_MEMBERSHIP_PERIOD,
   PRICINGS_PERIODS_BY_TYPES,
   DEFAULT_MEMBERSHIPS_LIMIT,
   PERIODS_NAME_BY_PRICING_PERIOD,
   PERIODS_CAPITALIZED_NAME_BY_PRICING_PERIOD,
   PERIODS_NAME_WITH_DAYS_BY_PRICING_PERIOD,
   COMPARE_FIRST_PAGE_COUNT,
   getColsCount,
   useImageBrightness,
   sortBenefitsByDefault,
   PRICINGS_PERIOD_TEXT_NAMES,
}
